// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { __decorate } from 'tslib';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { sharedStyles } from '../../styles';
import { decimalSeparator } from '../../utils/decimal-separator.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import {
  dispatchAttributeChangedEventTypesafe,
  event,
  ShuiLitElement,
} from '../../utils/event-decorator.js';
import { ClosingEvent, KeyPressEvent } from '../../utils/events.js';
import { FocusBlurMixin } from '../../utils/focus-blur-mixin.js';
import numericKeypadStyles from './sh-numeric-keypad.lit.scss.js';
export const NumericKeypadListOfProperties = [
  'visible',
  'condensed',
  'target',
  'decimals',
  'disabled',
  'europeanNumberConvention',
];
/**
 * @fires visible-changed {VisiblePropertyChangedEvent} - *hide Emitted when visible changes.
 * @fires condensed-changed {CondensedPropertyChangedEvent} - *hide Emitted when condensed changes.
 * @fires target-changed {TargetPropertyChangedEvent} - *hide Emitted when target changes.
 * @fires decimals-changed {DecimalsPropertyChangedEvent} - *hide Emitted when decimals changes.
 * @fires disabled-changed {DisabledPropertyChangedEvent} - *hide Emitted when disabled changes.
 * @fires european-number-convention-changed {EuropeanNumberConventionPropertyChangedEvent} - *hide Emitted when europeanNumberConvention changes.
 * @fires key-press {KeyPressEvent} - *hide Emitted when the keypad key is press(clicked/enter button pressed) on keypad key.
 * @fires closing {ClosingEvent} - *hide Emitted when the keypad key first key and last key is tabbed.
 */
let SHNumericKeypad = class SHNumericKeypad extends FocusBlurMixin(ShuiLitElement) {
  constructor() {
    super(...arguments);
    /** @ignore */
    this.decimalSeparator = '.';
    /** @ignore */
    this.isKeypadButtonFocused = false;
  }
  static get styles() {
    return [sharedStyles, numericKeypadStyles];
  }
  render() {
    return html`
      <div class="functions-wrapper">
        <slot name="functions" id="functionsSlot" @slotchange=${this.addMargins}></slot>
      </div>
      <div class="keypad-main-frame">
        ${this.createKeypadContent()}
        <div
          class="circle-buttons erase-button dont-disable"
          tabindex="0"
          @click="${(e) => {
            this.circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this.EnterKeyIsPressedOnButton(e, e.target);
          }}"
          @keydown="${(e) => {
            this.keyDownOnEraseButton(e);
          }}"
        >
          <sh-icon icon="delete-number" tabindex="-1"></sh-icon>
        </div>
      </div>
      <sh-divider class="divider" spacing="m"></sh-divider>
      <div class="footer-wrapper">
        <slot
          name="footer"
          id="footer"
          @slotchange=${this.hideOrDisplayDividerBasedOnFooter}
        ></slot>
      </div>
    `;
  }
  connectedCallback() {
    super.connectedCallback();
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
  }
  update(changedProperties) {
    if (changedProperties.has('europeanNumberConvention')) {
      this.europeanNumberConventionObserver();
    }
    super.update(changedProperties);
  }
  updated(changedProperties) {
    dispatchAttributeChangedEventTypesafe(this, changedProperties, NumericKeypadListOfProperties);
  }
  createKeypadContent() {
    const keypadNumbersArray = [];
    const valueArray = ['1', '2', '3', '4', '5', '6', '7', '8', '9', this.decimalSeparator, '0'];
    for (let index = 0; index < valueArray.length; index++) {
      keypadNumbersArray.push(html`
        <div
          value=${valueArray[index]}
          class="circle-buttons ${valueArray[index] === this.decimalSeparator
            ? 'dont-disable decimals-point'
            : ''}"
          tabindex=${this.disabled && valueArray[index] !== this.decimalSeparator ? -1 : 0}
          @click="${(e) => {
            this.circleButtonClick(e);
          }}"
          @keyup="${(e) => {
            this.EnterKeyIsPressedOnButton(e, e.target);
          }}"
          @keydown="${(e) => {
            if (valueArray[index] == '1') {
              this.keyDownOnNumberOne(e);
            }
          }}"
        >
          ${valueArray[index]}
        </div>
      `);
    }
    return keypadNumbersArray;
  }
  circleButtonClick(e) {
    const shIconTag = 'SH-ICON';
    const isEraseButton =
      e.target.tagName === shIconTag || e.target.classList.contains('erase-button');
    const detail = {
      isEraseButton: isEraseButton,
      keyValueString: isEraseButton ? '' : e.target.getAttribute('value'),
      changeFocus: !this.isKeypadButtonFocused,
    };
    this.keyPressEvent.emit(new KeyPressEvent({ detail }));
    this.isKeypadButtonFocused = false;
  }
  keyDownOnEraseButton(e) {
    if (e.code === 'Tab' && !e.shiftKey) {
      const checkfooterNodes = true;
      if (!this.slotNodesPresent(checkfooterNodes)) {
        this.closingEvent.emit(new ClosingEvent());
      }
    }
  }
  keyDownOnNumberOne(e) {
    if (e.code === 'Tab' && e.shiftKey) {
      const checkfooterNodes = false;
      if (!this.slotNodesPresent(checkfooterNodes)) {
        this.closingEvent.emit(new ClosingEvent());
      }
    }
  }
  slotNodesPresent(checkfooterNodes) {
    var _a, _b;
    if (checkfooterNodes) {
      const footerSlot =
        (_a = this.shadowRoot) === null || _a === void 0
          ? void 0
          : _a.querySelector('[name="footer"]');
      if (footerSlot !== null) {
        const footerNodes =
          footerSlot === null || footerSlot === void 0
            ? void 0
            : footerSlot.assignedNodes({
                flatten: true,
              });
        if (footerNodes.length !== 0) {
          return true;
        }
      }
    } else {
      const functionSlot =
        (_b = this.shadowRoot) === null || _b === void 0
          ? void 0
          : _b.querySelector('[name="functions"]');
      if (functionSlot !== null) {
        const functionNodes = functionSlot.assignedNodes({
          flatten: true,
        });
        if (functionNodes.length !== 0) {
          return true;
        }
      }
    }
    return false;
  }
  EnterKeyIsPressedOnButton(e, element) {
    const shIconTag = 'SH-ICON';
    if (
      e.target.tagName !== shIconTag &&
      (((e.code === 'Enter' || e.code === 'NumpadEnter') && !this.disabled) ||
        ((e.code === 'Enter' || e.code === 'NumpadEnter') &&
          element.classList.contains('dont-disable')))
    ) {
      element.focus();
      this.isKeypadButtonFocused = true;
      element.click();
    }
  }
  hideOrDisplayDividerBasedOnFooter() {
    var _a, _b, _c, _d;
    const footerNodes =
      (_b =
        (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('#footer')) ===
        null || _b === void 0
        ? void 0
        : _b.assignedNodes({
            flatten: true,
          });
    if ((footerNodes === null || footerNodes === void 0 ? void 0 : footerNodes.length) !== 0) {
      ((_c = this.shadowRoot) === null || _c === void 0
        ? void 0
        : _c.querySelector('.divider')
      ).style.display = 'initial';
    } else {
      ((_d = this.shadowRoot) === null || _d === void 0
        ? void 0
        : _d.querySelector('.divider')
      ).style.display = 'none';
    }
  }
  europeanNumberConventionObserver() {
    if (typeof this.europeanNumberConvention !== 'boolean') {
      this.decimalSeparator = decimalSeparator;
    } else {
      this.decimalSeparator = this.europeanNumberConvention ? ',' : '.';
    }
  }
  addMargins() {
    var _a, _b, _c;
    const functionsWrapper =
      (_a = this.shadowRoot) === null || _a === void 0
        ? void 0
        : _a.querySelector('.functions-wrapper');
    const functionNodes =
      (_c =
        (_b = this.shadowRoot) === null || _b === void 0
          ? void 0
          : _b.querySelector('#functionsSlot')) === null || _c === void 0
        ? void 0
        : _c.assignedNodes({
            flatten: true,
          });
    functionsWrapper.style.marginBottom =
      (functionNodes === null || functionNodes === void 0 ? void 0 : functionNodes.length) !== 0
        ? '1rem'
        : '0rem';
  }
};
__decorate([event()], SHNumericKeypad.prototype, 'visibleChangedEvent', void 0);
__decorate([event()], SHNumericKeypad.prototype, 'condensedChangedEvent', void 0);
__decorate([event()], SHNumericKeypad.prototype, 'targetChangedEvent', void 0);
__decorate([event()], SHNumericKeypad.prototype, 'decimalsChangedEvent', void 0);
__decorate([event()], SHNumericKeypad.prototype, 'disabledChangedEvent', void 0);
__decorate([event()], SHNumericKeypad.prototype, 'europeanNumberConventionChangedEvent', void 0);
__decorate([event()], SHNumericKeypad.prototype, 'keyPressEvent', void 0);
__decorate([event()], SHNumericKeypad.prototype, 'closingEvent', void 0);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHNumericKeypad.prototype,
  'visible',
  void 0
);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHNumericKeypad.prototype,
  'condensed',
  void 0
);
__decorate(
  [property({ type: String, reflect: true })],
  SHNumericKeypad.prototype,
  'target',
  void 0
);
__decorate(
  [property({ type: String, reflect: true })],
  SHNumericKeypad.prototype,
  'decimals',
  void 0
);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHNumericKeypad.prototype,
  'disabled',
  void 0
);
__decorate(
  [property({ type: Boolean, reflect: true, attribute: 'european-number-convention' })],
  SHNumericKeypad.prototype,
  'europeanNumberConvention',
  void 0
);
SHNumericKeypad = __decorate([customElement('sh-numeric-keypad')], SHNumericKeypad);
export { SHNumericKeypad };
